import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { InputField } from 'app/components/Core';
import { useFetch } from 'app/utils/hooks';
import { addAlertAction } from 'app/store/Alerts/slice';
import { useAppDispatch, useAppSelector } from 'app/store';
import { getInitiateClaimSocietyRecordsLink } from 'app/store/Profile/selectors';
import {
  defaultValuesForEmail,
  V3EmailFormSchema,
} from 'app/components/Profile/Ids/ClaimDialogs/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { V3EmailClaimResponse } from '../types';

export interface EmailClaimDialogProps {
  open: boolean;
  societyId: string | undefined;
  societyName?: string;
  onClose: (needConfirm?: boolean, requestGuid?: string) => void;
}

export const EmailClaimDialog = ({
  open,
  societyId,
  societyName,
  onClose,
}: EmailClaimDialogProps) => {
  const dispatch = useAppDispatch();
  const link = useAppSelector(getInitiateClaimSocietyRecordsLink);
  const { callAPI } = useFetch<{ email: string }, V3EmailClaimResponse>();

  const {
    handleSubmit,
    reset,
    register,
    formState: { isValid, isSubmitting, errors },
  } = useForm<{ email: string }>({
    defaultValues: defaultValuesForEmail,
    mode: 'all',
    resolver: yupResolver(V3EmailFormSchema),
  });

  const handleClose = (needConfirm?: boolean, requestGuid?: string) => {
    onClose(needConfirm, requestGuid);
    reset(defaultValuesForEmail);
  };

  const onSubmit = handleSubmit(async (data: { email: string }) => {
    try {
      if (link && societyId) {
        const { response } = await callAPI(
          link?.href.replace('{societyId}', societyId),
          data
        );
        dispatch(
          addAlertAction({
            message: `We sent you an email with verification code. Please check your email (${data.email})`,
          })
        );

        handleClose(true, response.verification.item.requestGuid);
      }
    } catch (error) {
      dispatch(addAlertAction({ message: 'V3 verification of email failed' }));
    }
  });

  return (
    <Modal isOpen={open} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent data-testid="dialog">
        <ModalHeader data-testid="dialog-title" title="Society Connection">
          Society Connection
        </ModalHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            return onSubmit(e);
          }}
        >
          <ModalBody>
            <VStack spacing={4}>
              <Text fontSize="md" lineHeight="25px" pb={4}>
                If you are missing any {societyName} IDs, enter the email
                address you use with {societyName} and we&lsquo;ll send you a
                code to complete ID verification.
              </Text>
              <InputField
                label="Email"
                type="email"
                overflow="hidden"
                textOverflow="ellipsis"
                {...register('email')}
                data-testid="email"
                isInvalid={Boolean(errors?.email)}
                errorMessage={errors?.email?.message}
                errorMessageProps={{ 'data-testid': 'email-error-text' }}
              />
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Stack spacing={2} direction="row">
              <Button variant="secondary" onClick={() => handleClose()}>
                Cancel
              </Button>
              <Button type="submit" isDisabled={!isValid || isSubmitting}>
                Send
              </Button>
            </Stack>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
